import { useRouterContext } from '@speedlo/hooks'
import { Base64 } from 'js-base64'
import React from 'react'

import { useRoot } from '../app/useRoot'
import { DeliveryTypeEnum } from '../graph/generated'
import { useOrderCreate } from '../segments/map/hooks/useOrderCreate'

/**
 * Side effects for handling url params coming from official portal.
 *
 * Two modes:
 *   ?pickup ... Show pickup map by default or redirect to the menu when only single branch is recognized
 *   ?address=value ... Use given address as search value and redirect to the menu when the address can be delivered to
 */
export function usePortalNavigation(isMobile = false) {
  const { history } = useRouterContext()
  const { order, location, business } = useRoot()
  const alreadyCreatedOrder = React.useRef(false)
  const [addressFromPortal, setAddressFromPortal] = React.useState<
    Maybe<string>
  >(null)

  const { createPickupOrder, createDeliveryOrder } = useOrderCreate(() =>
    history.push('/order'),
  )

  const info = React.useMemo(() => {
    const url = new window.URL(window.location.href)
    const isPickup = url.searchParams.has('pickup')
    const address = url.searchParams.get('address')
    const portalDeliveryMobile = url.searchParams.has('portal-delivery')

    return {
      isPickup,
      portalDeliveryMobile,
      searchAddress: address && decodeAddress(address),
    }
  }, [])

  React.useEffect(
    function handlePickup() {
      if (info.isPickup) {
        order.useOrderMethod(DeliveryTypeEnum.Pickup)

        if (!business.isBrand && business.branchId) {
          !alreadyCreatedOrder.current && createPickupOrder(business.branchId)
          alreadyCreatedOrder.current = true
          return
        }

        if (isMobile && business.isBrand) {
          history.push('/pickup')
        }
      }
    },
    [
      order,
      isMobile,
      history,
      info.isPickup,
      business.isBrand,
      business.branchId,
      createPickupOrder,
    ],
  )

  React.useEffect(
    function handleDelivery() {
      const hasAddressFromPortal = Boolean(info.searchAddress)

      if (hasAddressFromPortal) {
        order.useOrderMethod(DeliveryTypeEnum.Messenger)

        location.resetResolution()
        location.setInitialSearchAddress(info.searchAddress)
        location.forceResolve(info.searchAddress)

        setAddressFromPortal(info.searchAddress)

        if (isMobile) {
          // on mobile, the location resolving starts only on the delivery map page
          // but it’s also necessary to somehow keep the notice that the redirect comes from the portal
          // so that the next effect would trigger on the mobile delivery page
          history.push('/delivery?portal-delivery')
        }
      }
    },
    [history, info.searchAddress, isMobile, location, order],
  )

  React.useEffect(
    function redirectOnValidAddress() {
      const cameFromPortal = Boolean(
        info.searchAddress || info.portalDeliveryMobile,
      )

      if (
        cameFromPortal &&
        location.isDeliveryAddress &&
        location.resolvedFor === addressFromPortal
      ) {
        !alreadyCreatedOrder.current && createDeliveryOrder()
        alreadyCreatedOrder.current = true
      }
    },
    [
      addressFromPortal,
      info.searchAddress,
      createDeliveryOrder,
      location.resolvedFor,
      info.portalDeliveryMobile,
      location.isDeliveryAddress,
    ],
  )
}

function decodeAddress(encodedAddress: string) {
  try {
    return Base64.decode(encodedAddress)
  } catch {
    return null
  }
}
